import { handleErrors } from "./errors";
import { validateRecaptchaToken } from "./recaptcha";
import type { Brand, Field, Settings, Value, _EventRequest, _SFMCValue, _UnparsedError } from "./types";

export const submit = {
  async DE(settings: Settings) {
    const urls: { [key: string]: string } = {
      WWW: "https://cloud.email.wwwincnews.com/form",
      BTS: "https://cloud.email.batesfootwear.com/form",
      CAT: "https://cloud.email.catfootwear.com/form",
      CHA: "https://cloud.email.chacos.com/form",
      HDF: "https://cloud.email.harley-davidsonfootwear.com/form",
      HUS: "https://cloud.email.hushpuppies.com/form",
      HYT: "https://cloud.email.hytest.com/form",
      KED: "https://cloud.email.keds.com/form",
      MER: "https://cloud.email.merrell.com/form",
      PRO: "https://cloud.email.prokeds.com/form",
      SAU: "https://cloud.email.saucony.com/form",
      SPE: "https://cloud.email.sperry.com/form",
      WOL: "https://cloud.email.wolverine.com/form"
    };

    const url = urls[settings.brand];

    const properties: _SFMCValue[] = Object.entries(settings.internal.data).map(([key, value]) => ({
      Name: key,
      Value: value
    }));

    properties.push({ Name: "URL", Value: window.location.href });

    if (settings.submitRecaptchaToken && settings.recaptchaToken) {
      properties.push({ Name: "RecaptchaToken", Value: settings.recaptchaToken });
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        dataExtension: settings.dataExtension,
        properties
      })
    });

    const data = await response.json();

    if (data.status === "Error") {
      if (!data.errorMessage && data.statusMessage) {
        data.errorMessage = data.statusMessage;
      }

      throw [
        {
          errorCode: data.errorCode,
          errorMessage: data.errorMessage
        }
      ];
    }

    return data;
  },

  async MC(settings: Settings) {
    const { data } = settings.internal;
    const country = data.Country || data.CountryCode || "US";

    const supportedBrandNames: { [key in Brand]+?: string } = {
      CAT: "CAT",
      CHA: "Chaco",
      MER: "Merrell",
      SAU: "Sauc",
      WOL: "Wolverine"
    };

    const brandName = supportedBrandNames[settings.brand];

    if (!brandName) {
      throw [
        {
          errorCode: 3,
          errorMessage: "Invalid brand: Must be CAT, CHA, MER, SAU or WOL for method = MC"
        }
      ];
    }

    const key = `${data.EmailAddress}-${brandName}-${country}`;

    const request: _EventRequest = {
      brand: settings.internal.brandFullName,
      edk: settings.eventDefinitionKey || (data.EventDefinitionKey as string),
      key,
      data: {
        LanguageCode: `${data.Language}-${country}`,
        SubscriberKey: key,
        Source: "EventCampaign",
        ...data
      }
    };

    const response = await fetch("https://cloud.email.wwwincnews.com/event", {
      method: "POST",
      body: JSON.stringify(request)
    }).then((res) => res.json());

    if (response.error) {
      throw [
        {
          errorCode: response.errorCode,
          errorMessage: response.error
        }
      ];
    }

    if (!response.success) {
      throw [
        {
          errorCode: 5,
          errorMessage: "Something went wrong"
        }
      ];
    }

    return response;
  },

  async BM(settings: Settings) {
    const { emailPreferencesURL: url } = settings;

    const email = settings.internal.data.EmailAddress;

    if (!email || typeof email !== "string") {
      const error: _UnparsedError = {
        errorCode: 2,
        errorMessage: "EmailAddress field required when method = BM or MC"
      };

      throw error;
    }

    const body = new FormData();
    body.append("OptStatus", "I");
    body.append("EmailAddress", email);
    body.append("g-recaptcha-response", settings.recaptchaToken);

    const response = await fetch(url, {
      method: "POST",
      body
    });

    const data = await response.text();

    return data;
  }
};

export function formatFieldData(field: Field): Value {
  const name = field.dataset.field;
  let value: string | number | boolean | undefined;

  if (field.tagName === "DIV" || field.tagName === "FIELDSET") {
    const radios = Array.from(field.querySelectorAll("input[type=radio]") as NodeListOf<HTMLInputElement>);
    const checked = radios.filter((radio) => radio.checked)[0];

    if (checked) {
      value = checked.value;
    } else {
      value = null;
    }
  } else {
    if (field.type === "checkbox" && field instanceof HTMLInputElement) {
      value = field.checked;
    } else {
      if (field.value) {
        if (isNaN(Number(field.value))) {
          value = field.value;
        } else {
          value = Number(field.value);
        }
      } else {
        value = null;
      }
    }
  }

  if (value === undefined) {
    value = "";
  }

  if (typeof value === "string") {
    value = value.trim();
  }

  if (field.type === "email" && typeof value === "string") {
    value = value.toLowerCase();
  }

  return { name, value };
}

export async function handleSubmit(settings: Settings) {
  try {
    if (settings.recaptchaToken) {
      const validationResults = await validateRecaptchaToken(settings.recaptchaToken);
      settings.events.recaptchaVerification(validationResults);

      if (!validationResults.success) {
        const error: _UnparsedError = {
          errorCode: 4,
          errorMessage: "Invalid reCAPTCHA token"
        };

        throw error;
      }
    }

    const response = await submit[settings.method](settings);

    settings.events.success(response);

    const email = settings.internal.data.EmailAddress;
    const phone = settings.internal.data.Phone;
    const event = "trackEvent-emailSubmit";
    const salt = "wwwga4salt";

    window.dataLayer = window.dataLayer || [];

    if (email) {
      window.dataLayer.push({
        event,
        emailform: settings.rawName,
        lead_type: "email",
        email: await window.hashStr(String(email), salt)
      });
    }

    if (phone) {
      window.dataLayer.push({
        event,
        emailform: settings.rawName,
        lead_type: "sms",
        phone_number: await window.hashStr(String(phone), salt)
      });
    }

    if (settings.redirect) {
      window.location.href = settings.redirect;
    }
  } catch (errors) {
    if (!Array.isArray(errors)) {
      errors = [errors];
    }

    const filteredErrors: _UnparsedError[] = errors.filter(
      (error: _UnparsedError | Error) => "errorMessage" in error && "errorCode" in error
    );

    if (filteredErrors.length) {
      settings.internal.errors.push(...filteredErrors);
      handleErrors(settings.internal.errors, settings);
    } else {
      errors.forEach((error: Error) => {
        console.error(error);
      });
    }
  }
}
